/**
 * CountryFiscalConfigPanel — DB-backed, professional ERP grid
 *
 * Columns: Flag · Country · Currency · Number Format · Date · Fiscal Year
 *          · Tax System · Tax Rate · Tax Registration · Notes
 *
 * Color coding:
 *   GST countries     → green
 *   VAT countries     → blue
 *   Sales Tax (US)    → orange
 *   Multi-Tax (BRA)   → red
 *   Consumption Tax   → purple
 *   SST (Malaysia)    → teal
 *
 * Admin can edit TaxSystem / TaxRatePrimary / TaxRegTerm / TaxNotes inline.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, RefreshCw, Edit2, Save, X, AlertCircle,
  DollarSign, Calendar, Hash, FileText, ChevronDown, ChevronRight, Globe
} from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import '../styles/erp-theme.css';
import { API, apiFetch } from '../lib/api';



// ── Tax system badge metadata ─────────────────────────────────────────────────
const TAX_META = {
  'GST':              { color: '#059669', bg: '#d1fae5', label: 'GST'         },
  'GST / HST':        { color: '#0d9488', bg: '#ccfbf1', label: 'GST/HST'     },
  'GST / FED':        { color: '#059669', bg: '#d1fae5', label: 'GST/FED'     },
  'VAT':              { color: '#2563eb', bg: '#dbeafe', label: 'VAT'         },
  'VAT (PPN)':        { color: '#2563eb', bg: '#dbeafe', label: 'VAT/PPN'     },
  'Sales Tax':        { color: '#ea580c', bg: '#ffedd5', label: 'Sales Tax'   },
  'Multi-Tax (IBS/CBS)':{ color:'#dc2626', bg:'#fee2e2', label: 'Multi-Tax'  },
  'Consumption Tax':  { color: '#7c3aed', bg: '#ede9fe', label: 'Cons. Tax'   },
  'SST':              { color: '#0f766e', bg: '#ccfbf1', label: 'SST'         },
};
function getTaxMeta(ts) {
  if (!ts) return { color: '#64748b', bg: '#f1f5f9', label: ts || '—' };
  return TAX_META[ts] || { color: '#64748b', bg: '#f1f5f9', label: ts };
}

// ── Fiscal year badge ─────────────────────────────────────────────────────────
const FISCAL_COLORS = {
  '4-3':  '#059669',  // Apr-Mar (India, UK, Japan, NZ, Pakistan, Bangladesh, Kenya)
  '7-6':  '#d97706',  // Jul-Jun (Australia)
  '1-12': '#2563eb',  // Jan-Dec (most)
  '3-2':  '#7c3aed',  // Mar-Feb (SA)
};
function fiscalColor(start, end) {
  return FISCAL_COLORS[`${start}-${end}`] || '#64748b';
}

const EMPTY_EDIT = { TaxSystem:'', TaxRatePrimary:'', TaxRegTerm:'', TaxNotes:'' };

export default function CountryFiscalConfigPanel() {
  const [data,    setData]    = useState({ rows: [], total: 0, page: 1, pageSize: 25, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [taxFilter, setTaxFilter] = useState('ALL');
  const [page,    setPage]    = useState(1);
  const PAGE_SIZE = 25;
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [editRow,   setEditRow]   = useState(null);
  const [editVals,  setEditVals]  = useState(EMPTY_EDIT);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);

  const rows = data.rows || [];

  const load = useCallback(async (p = page, s = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, pageSize: PAGE_SIZE, search: s });
      const r = await apiFetch(`${API}/country-config?${params}`);
      const d = await r.json();
      // Handle both paginated response {rows, total, ...} and legacy flat array
      if (Array.isArray(d)) {
        setData({ rows: d, total: d.length, page: 1, pageSize: d.length, totalPages: 1 });
      } else {
        setData(d);
      }
    } catch (e) {
      erpToast.error(`Load failed: ${e.message}`);
    }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(1, ''); }, []);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
    load(1, searchInput);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    load(newPage, search);
  };

  const seedAll = async () => {
    setSeeding(true);
    try {
      const r = await apiFetch(`${API}/country-config/seed-all`, { method: 'POST' });
      const d = await r.json();
      if (d.error) { erpToast.error(d.error); }
      else { erpToast.success(`Seeded ${d.count} countries`); load(1, search); }
    } catch (e) { erpToast.error(e.message); }
    setSeeding(false);
  };

  // Unique tax systems for filter chips (from current page — use full data in backend)
  const taxSystems = useMemo(() => {
    const seen = new Set(['ALL']);
    const out  = [{ key: 'ALL', label: 'All' }];
    rows.forEach(r => {
      const ts = (r.TaxSystem || r.taxSystem || '').split(/[/(]/)[0].trim();
      if (ts && !seen.has(ts)) { seen.add(ts); out.push({ key: ts, label: ts }); }
    });
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    if (taxFilter === 'ALL') return rows;
    return rows.filter(r => (r.TaxSystem||'').startsWith(taxFilter));
  }, [rows, taxFilter]);

  const startEdit = (row) => {
    setEditRow(row.CountryCode);
    setEditVals({
      TaxSystem:      row.TaxSystem      || '',
      TaxRatePrimary: row.TaxRatePrimary != null ? String(row.TaxRatePrimary) : '',
      TaxRegTerm:     row.TaxRegTerm     || '',
      TaxNotes:       row.TaxNotes       || '',
    });
  };
  const cancelEdit = () => { setEditRow(null); setEditVals(EMPTY_EDIT); };

  const saveEdit = async (code) => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API}/country-config/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          TaxSystem:      editVals.TaxSystem      || undefined,
          TaxRatePrimary: editVals.TaxRatePrimary ? parseFloat(editVals.TaxRatePrimary) : undefined,
          TaxRegTerm:     editVals.TaxRegTerm      || undefined,
          TaxNotes:       editVals.TaxNotes        || undefined,
        }),
      });
      const d = await r.json();
      if (d.error) { erpToast.error(d.error); }
      else { erpToast.success(`${code} updated`); cancelEdit(); load(page, search); }
    } catch (e) { erpToast.error(e.message); }
    setSaving(false);
  };

  const toggleNotes = (code) => {
    setExpandedNotes(prev => {
      const s = new Set(prev);
      if (s.has(code)) s.delete(code); else s.add(code);
      return s;
    });
  };

  /* ── render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 200px)', minHeight:600 }}>

      {/* ── toolbar ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid var(--border,#e2e8f0)',
        background: 'var(--surface-2,#f8fafc)', display:'flex', alignItems:'center',
        gap: 8, flexWrap:'wrap', flexShrink: 0,
      }}>
        <Globe size={14} color="#3b82f6" />
        <span style={{ fontWeight:700, fontSize:'0.86rem', color:'var(--text-primary,#0f172a)' }}>
          Country Configuration
        </span>
        <span style={{ fontSize:'0.71rem', color:'#64748b', marginRight: 8 }}>
          {data.total} countries · currency, number format, fiscal year, tax compliance &amp; timezone
        </span>

        {/* search */}
        <div style={{ position:'relative', flex:1, minWidth:200 }}>
          <Search size={13} color="#94a3b8" style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} />
          <input
            className="form-input"
            placeholder="Search country, currency, tax system, timezone…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ paddingLeft: 30, fontSize:'0.79rem' }}
            data-testid="country-config-search"
          />
        </div>
        <button className="erp-btn" onClick={handleSearch} style={{ fontSize:'0.79rem' }} data-testid="country-config-search-btn">
          <Search size={11} /> Search
        </button>
        <button className="erp-btn" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); load(1, ''); }} style={{ fontSize:'0.79rem' }} data-testid="country-config-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
        <button
          className="erp-btn"
          onClick={seedAll}
          disabled={seeding}
          style={{ fontSize:'0.79rem', background:'#eff6ff', color:'#1d4ed8', border:'1px solid #bfdbfe' }}
          data-testid="country-config-seed-all"
          title="Upsert all 195 countries into DB"
        >
          {seeding ? '⏳ Seeding…' : '🌍 Seed 195 Countries'}
        </button>
      </div>

      {/* ── tax system filter chips ───────────────────────────────────────── */}
      <div style={{ padding:'8px 14px', borderBottom:'1px solid var(--border,#e2e8f0)', display:'flex', gap:6, flexWrap:'wrap', flexShrink:0 }}>
        {taxSystems.map(ts => {
          const active = taxFilter === ts.key;
          const meta   = ts.key === 'ALL' ? { color:'#3b82f6', bg:'#dbeafe' } : getTaxMeta(ts.key);
          return (
            <button
              key={ts.key}
              onClick={() => setTaxFilter(ts.key)}
              data-testid={`tax-filter-${ts.key}`}
              style={{
                fontSize:'0.71rem', padding:'3px 10px', borderRadius: 20,
                border: active ? `1.5px solid ${meta.color}` : '1px solid #e2e8f0',
                background: active ? meta.bg : '#fff',
                color: active ? meta.color : '#64748b',
                cursor:'pointer', fontWeight: active ? 700 : 500,
              }}
            >{ts.label}{active && ts.key !== 'ALL' ? ` (${filtered.length})` : ''}</button>
          );
        })}
      </div>

      {/* ── grid ─────────────────────────────────────────────────────────── */}
          <div className="country-config-table-wrap" style={{ flex:1, overflow:'auto', minHeight:380 }}>
        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'#94a3b8', fontSize:'0.86rem' }}>
            <RefreshCw size={18} style={{ animation:'spin1s 1s linear infinite', marginRight:8 }} />
            Loading country configuration…
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.79rem', minWidth: 900 }} className="erp-grid cc-pivot-table">
            <style>{`
              /* Pivot: freeze first 3 columns */
              .cc-pivot-table th.cc-sticky,
              .cc-pivot-table td.cc-sticky {
                position: sticky;
                z-index: 2;
                background: inherit;
              }
              .cc-pivot-table thead th.cc-sticky { z-index: 4; background: var(--erp-grid-header-bg, #1e3a5f); }
              .cc-pivot-table tbody tr:nth-child(even) td.cc-sticky { background: var(--erp-grid-row-alt, #f8fafc); }
              .cc-pivot-table tbody tr:hover td.cc-sticky { background: var(--erp-grid-row-hover, #eff6ff); }
              /* col 0 = Actions (60px), col 1 = Country (120px), col 2 = Currency (110px) */
              .cc-pivot-table .cc-col-0 { left: 0;    width: 60px;  min-width: 60px;  max-width: 60px;  text-align:center; }
              .cc-pivot-table .cc-col-1 { left: 60px; width: 120px; min-width: 120px; }
              .cc-pivot-table .cc-col-2 { left: 180px; width: 110px; min-width: 110px; box-shadow: 2px 0 4px rgba(0,0,0,0.06); }
            `}</style>
            <thead>
              <tr>
                <th className="cc-sticky cc-col-0" style={{ position:'sticky', top:0, zIndex:5 }}>Action</th>
                <th className="cc-sticky cc-col-1" style={{ position:'sticky', top:0, zIndex:5 }}>Country</th>
                <th className="cc-sticky cc-col-2" style={{ position:'sticky', top:0, zIndex:5 }}>
                  <DollarSign size={11} style={{ marginRight:3 }} />Currency
                </th>
                <th style={{ width:120, position:'sticky', top:0, zIndex:2 }}>
                  <Hash size={11} style={{ marginRight:3 }} />Number Format
                </th>
                <th style={{ width:90, position:'sticky', top:0, zIndex:2 }}>
                  <Calendar size={11} style={{ marginRight:3 }} />Date Format
                </th>
                <th style={{ width:110, position:'sticky', top:0, zIndex:2 }}>
                  <Calendar size={11} style={{ marginRight:3 }} />Fiscal Year
                </th>
                <th style={{ width:130, position:'sticky', top:0, zIndex:2 }}>Tax System</th>
                <th style={{ width:90, position:'sticky', top:0, zIndex:2 }}>Rate</th>
                <th style={{ width:140, position:'sticky', top:0, zIndex:2 }}>
                  🌐 Timezone
                </th>
                <th style={{ width:160, position:'sticky', top:0, zIndex:2 }}>
                  <FileText size={11} style={{ marginRight:3 }} />Tax Registration
                </th>
                <th style={{ width:200, position:'sticky', top:0, zIndex:2 }}>Compliance Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <CountryConfigRow
                  key={row.CountryCode}
                  row={row}
                  isEditing={editRow === row.CountryCode}
                  editVals={editVals}
                  onEditChange={(field, val) => setEditVals(v => ({ ...v, [field]: val }))}
                  saving={saving}
                  onSave={() => saveEdit(row.CountryCode)}
                  onStartEdit={() => startEdit(row)}
                  onCancel={cancelEdit}
                  notesOpen={expandedNotes.has(row.CountryCode)}
                  onToggleNotes={() => toggleNotes(row.CountryCode)}
                />
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={11} style={{ padding:40, textAlign:'center', color:'#94a3b8', fontSize:'0.86rem' }}>
                    <AlertCircle size={20} style={{ marginBottom:8, display:'block', margin:'0 auto 8px' }} />
                    No countries match the filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
          </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {data.totalPages > 1 && (
        <div style={{
          padding:'8px 14px', borderTop:'1px solid var(--border,#e2e8f0)',
          background:'var(--surface-2,#f8fafc)', display:'flex', alignItems:'center', gap:6, flexShrink:0
        }}>
          <span style={{ fontSize:'0.75rem', color:'#64748b' }}>
            Page {data.page} of {data.totalPages} · {data.total} countries total
          </span>
          <button
            className="erp-btn"
            onClick={() => handlePageChange(1)}
            disabled={data.page <= 1}
            style={{ fontSize:'0.75rem', padding:'2px 8px' }}
          >«</button>
          <button
            className="erp-btn"
            onClick={() => handlePageChange(data.page - 1)}
            disabled={data.page <= 1}
            style={{ fontSize:'0.75rem', padding:'2px 8px' }}
          >‹ Prev</button>
          {/* Page numbers */}
          {Array.from({ length: Math.min(7, data.totalPages) }, (_, i) => {
            const p = Math.max(1, Math.min(data.totalPages - 6, data.page - 3)) + i;
            if (p > data.totalPages) return null;
            return (
              <button
                key={p}
                className="erp-btn"
                onClick={() => handlePageChange(p)}
                style={{
                  fontSize:'0.75rem', padding:'2px 8px',
                  background: p === data.page ? '#3b82f6' : undefined,
                  color: p === data.page ? '#fff' : undefined,
                  fontWeight: p === data.page ? 700 : undefined,
                }}
              >{p}</button>
            );
          })}
          <button
            className="erp-btn"
            onClick={() => handlePageChange(data.page + 1)}
            disabled={data.page >= data.totalPages}
            style={{ fontSize:'0.75rem', padding:'2px 8px' }}
          >Next ›</button>
          <button
            className="erp-btn"
            onClick={() => handlePageChange(data.totalPages)}
            disabled={data.page >= data.totalPages}
            style={{ fontSize:'0.75rem', padding:'2px 8px' }}
          >»</button>
        </div>
      )}

      {/* ── footer info ──────────────────────────────────────────────────── */}
      <div style={{
        padding:'6px 14px', borderTop:'1px solid var(--border,#e2e8f0)',
        background:'var(--surface-2,#f8fafc)', display:'flex', alignItems:'center', gap:12, flexShrink:0
      }}>
        <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
          {Object.entries(TAX_META).slice(0,5).map(([ts, m]) => (
            <span key={ts} style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.72rem' }}>
              <span style={{ width:8, height:8, borderRadius:2, background:m.bg, border:`1px solid ${m.color}40` }} />
              <span style={{ color: m.color, fontWeight:600 }}>{m.label}</span>
            </span>
          ))}
        </div>
        <div style={{ marginLeft:'auto', fontSize:'0.72rem', color:'#94a3b8' }}>
          Source: WebApp.CountryConfig · Click Edit to adjust tax info
        </div>
      </div>

      <style>{`@keyframes spin1s { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── CountryConfigRow ── sub-component extracted to reduce CountryFiscalConfigPanel complexity
function CountryConfigRow({ row, isEditing, editVals, onEditChange, saving, onSave, onStartEdit, onCancel, notesOpen, onToggleNotes }) {
  const code     = row.CountryCode;
  const taxMeta  = getTaxMeta(row.TaxSystem);
  const fCol     = fiscalColor(row.FiscalStart, row.FiscalEnd);
  const fiscalLbl = row.fiscal_label || `${row.FiscalStart}→${row.FiscalEnd}`;

  return (
    <React.Fragment>
      <tr style={{ borderBottom:'1px solid #f1f5f9', verticalAlign:'top' }}>

        {/* Actions — COL 0 (sticky) */}
        <td className="cc-sticky cc-col-0" style={{ padding:'6px 6px', textAlign:'center', background:'#fff' }}>
          {isEditing ? (
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <button className="erp-btn erp-btn-primary" onClick={onSave} disabled={saving}
                style={{ fontSize:'0.71rem', padding:'3px 6px' }} data-testid={`country-save-${code}`}>
                <Save size={10} />
              </button>
              <button className="erp-btn" onClick={onCancel} style={{ fontSize:'0.71rem', padding:'3px 6px' }}>
                <X size={10} />
              </button>
            </div>
          ) : (
            <button className="erp-btn" onClick={onStartEdit}
              style={{ fontSize:'0.71rem', padding:'3px 8px' }} data-testid={`country-edit-${code}`}>
              <Edit2 size={10} />
            </button>
          )}
        </td>

        {/* Country — COL 1 (sticky) */}
        <td className="cc-sticky cc-col-1" style={{ padding:'8px 10px', fontWeight:600, background:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ fontSize:'1rem' }}>{row.FlagEmoji || '🌐'}</span>
            <div>
              <div style={{ fontSize:'0.79rem', fontWeight:700 }}>{row.CountryName}</div>
              <div style={{ fontSize:'0.72rem', color:'#94a3b8', fontFamily:'monospace' }}>
                {code} · {row.PhoneCode}
              </div>
            </div>
          </div>
        </td>

        {/* Currency — COL 2 (sticky) */}
        <td className="cc-sticky cc-col-2" style={{ padding:'8px 10px', background:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontFamily:'monospace', fontWeight:800, fontSize:'0.93rem', color:'#0f172a', minWidth:24, textAlign:'right' }}>
              {row.CurrencySymbol}
            </span>
            <div>
              <div style={{ fontWeight:600, fontSize:'0.79rem' }}>{row.CurrencyCode}</div>
              <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>{row.NumDecimals} dec. places</div>
            </div>
          </div>
        </td>

        {/* Number Format */}
        <td style={{ padding:'8px 10px' }}>
          <div style={{ fontSize:'0.79rem', fontFamily:'monospace', color:'#334155' }}>
            {row.DecimalSep === ',' ? '1.000.000,00' : row.ThousandsSep === ' ' ? '1 000 000,00' : row.ThousandsSep === '.' ? '10,00,000.00' : '1,000,000.00'}
          </div>
          <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:1 }}>
            Dec: <b>{row.DecimalSep}</b> · Sep: <b>{row.ThousandsSep === ' ' ? '(space)' : row.ThousandsSep}</b>
          </div>
          <div style={{ fontSize:'0.72rem', color:'#94a3b8', fontFamily:'monospace' }}>{row.NumberLocale}</div>
        </td>

        {/* Date Format */}
        <td style={{ padding:'8px 10px', fontFamily:'monospace', fontSize:'0.79rem', color:'#334155' }}>
          {row.DateFormat}
        </td>

        {/* Fiscal Year */}
        <td style={{ padding:'8px 10px' }}>
          <span style={{
            display:'inline-flex', alignItems:'center', gap:4,
            background:`${fCol}18`, color:fCol, border:`1px solid ${fCol}40`,
            borderRadius:6, padding:'2px 8px', fontSize:'0.71rem', fontWeight:700,
          }}>
            <Calendar size={10} />{fiscalLbl}
          </span>
        </td>

        {/* Tax System */}
        <td style={{ padding:'8px 10px' }}>
          {isEditing ? (
            <input className="form-input" value={editVals.TaxSystem}
              onChange={e => onEditChange('TaxSystem', e.target.value)}
              style={{ fontSize:'0.79rem', width:'100%' }} />
          ) : (
            <span style={{ display:'inline-block', background:taxMeta.bg, color:taxMeta.color, border:`1px solid ${taxMeta.color}40`, borderRadius:6, padding:'3px 8px', fontSize:'0.71rem', fontWeight:700 }}>
              {taxMeta.label}
            </span>
          )}
          {!isEditing && row.TaxRateReduced && (
            <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>Also: {row.TaxRateReduced}</div>
          )}
        </td>

        {/* Tax Rate */}
        <td style={{ padding:'8px 10px' }}>
          {isEditing ? (
            <input className="form-input" value={editVals.TaxRatePrimary}
              onChange={e => onEditChange('TaxRatePrimary', e.target.value)}
              style={{ fontSize:'0.79rem', width:60 }} type="number" step="0.01" />
          ) : (
            row.TaxRatePrimary != null
              ? <span style={{ fontWeight:700, fontSize:'0.86rem', color:taxMeta.color }}>{row.TaxRatePrimary}%</span>
              : <span style={{ fontSize:'0.71rem', color:'#94a3b8' }}>Varies</span>
          )}
        </td>

        {/* Timezone */}
        <td style={{ padding:'8px 10px', fontFamily:'monospace', fontSize:'0.75rem', color:'#334155' }}>
          {row.Timezone ? (
            <div>
              <span style={{ fontSize:'0.75rem', fontWeight:600, color:'#0f172a' }}>
                {row.Timezone.split('/').pop().replace(/_/g, ' ')}
              </span>
              <div style={{ fontSize:'0.70rem', color:'#94a3b8' }}>{row.Timezone}</div>
            </div>
          ) : <span style={{ color:'#94a3b8' }}>—</span>}
        </td>

        {/* Tax Registration */}
        <td style={{ padding:'8px 10px' }}>
          {isEditing ? (
            <input className="form-input" value={editVals.TaxRegTerm}
              onChange={e => onEditChange('TaxRegTerm', e.target.value)}
              style={{ fontSize:'0.79rem', width:'100%' }} />
          ) : (
            <div>
              <span style={{ background:'#f1f5f9', color:'#334155', border:'1px solid #e2e8f0', borderRadius:4, padding:'2px 6px', fontSize:'0.71rem', fontWeight:700 }}>
                {row.TaxRegTerm || '—'}
              </span>
              {row.TaxRegFormat && (
                <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:3, fontFamily:'monospace' }}>{row.TaxRegFormat}</div>
              )}
            </div>
          )}
        </td>

        {/* Compliance Notes */}
        <td style={{ padding:'8px 10px', maxWidth:200 }}>
          {isEditing ? (
            <textarea className="form-input" value={editVals.TaxNotes}
              onChange={e => onEditChange('TaxNotes', e.target.value)}
              style={{ fontSize:'0.71rem', width:'100%', minHeight:60, resize:'vertical' }} />
          ) : (
            <div>
              <div style={{ fontSize:'0.71rem', color:'#475569', lineHeight:1.4, overflow:'hidden', maxHeight: notesOpen ? 'none' : 48 }}>
                {row.TaxNotes || '—'}
              </div>
              {(row.TaxNotes||'').length > 80 && (
                <button onClick={onToggleNotes}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#3b82f6', fontSize:'0.72rem', padding:'2px 0' }}>
                  {notesOpen ? '▲ less' : '▼ more'}
                </button>
              )}
            </div>
          )}
        </td>

      </tr>
    </React.Fragment>
  );
}
